import {
    FC,
    RefObject,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { Letter } from 'react-letter';
import classNames from 'classnames';

import type { LetterContent } from '@/stores';

import styles from './HTMLEmailWrapper.module.scss';
import { useSize } from '@/components/hooks';

const BODY_EXTRA_OFFSET_PX = 20;
const SCALE_MULTIPLIER = 1.2;
const SMALL_CONTENT_TO_WINDOW_SIZE = 3;

const useLetterContentHeight = (): {
    containerRef: RefObject<HTMLDivElement>;
    isScaled: boolean;
} => {
    const [isScaled, setIsScaled] = useState<boolean>(false);

    const { containerRef } = useSize<HTMLDivElement>();

    useLayoutEffect(() => {
        const iframe = containerRef?.current.querySelector('iframe');
        if (iframe) {
            iframe.style.display = 'none';
        }

        const fitIframeWithContentHeight = (): void => {
            iframe.style.display = 'block';
            const { body } = iframe?.contentDocument ?? { body: null };
            if (body) {
                // Measure natural dimensions before zoom is applied so that
                // isSmallContent and finalHeight are based on the true content size.
                const naturalHeight = body.scrollHeight;
                const contentWidth = body.scrollWidth;
                const containerWidth = containerRef.current.offsetWidth;

                let widthScale = 1;
                if (contentWidth > containerWidth && containerWidth > 0) {
                    widthScale = containerWidth / contentWidth;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (body.style as any).zoom = String(widthScale);
                }

                // Derive effective height from natural height scaled by widthScale
                // so the calculation is independent of whether zoom was applied.
                const effectiveHeight = naturalHeight * widthScale;
                const isSmallContent = window.innerHeight / effectiveHeight > SMALL_CONTENT_TO_WINDOW_SIZE;
                setIsScaled(isSmallContent);
                const finalHeight = (effectiveHeight + BODY_EXTRA_OFFSET_PX) * (isSmallContent ? SCALE_MULTIPLIER : 1);
                containerRef.current.style.height = `${finalHeight}px`;
                if (effectiveHeight < finalHeight) {
                    containerRef.current.style.overflowY = `hidden`;
                }
            }
        };

        iframe?.addEventListener('load', fitIframeWithContentHeight);
        return () => iframe?.removeEventListener('load', fitIframeWithContentHeight);
    }, []);

    return { containerRef, isScaled };
};

const LetterWrapper: FC<Omit<LetterContent, 'subject'>> = ({ html, plainText, attachments }) => {
    const { containerRef, isScaled } = useLetterContentHeight();

    return (
        <div
            ref={containerRef}
            className={classNames(styles['letter-wrapper'], { [styles.scaled]: isScaled })}
        >
            <Letter
                useIframe
                className={styles['inner-wrapper']}
                rewriteExternalResources={(url) => {
                    const content = attachments.find(({cid}) => cid === url);
                    return content?.link ?? url;
                }}
                html={html}
                text={plainText}
            />
        </div>
    );
};

export default LetterWrapper;
